import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import {
  CfsTemplateBaseComponent,
  CfsTemplateError,
  CfsTemplateMediaConfig,
  TemplateAnimationType,
  TemplateProperties,
  UrlContentSettings,
  VideoTemplateContentType
} from "../../../models";
import { VimeoPlayerComponent, YoutubePlayerComponent } from "../../external-link-video-player";
import { HtmlVideoPlayerComponent } from "./html-video-player";
import { defaultCfsTemplateMediaConfig } from "../cfs-template.config";

export type VideoPlayerComponent = HtmlVideoPlayerComponent | YoutubePlayerComponent | VimeoPlayerComponent;

@Component({
  selector: "lib-video-template",
  templateUrl: "./video-template.component.html",
  styleUrls: ["./video-template.component.scss"],
  standalone: true,
  imports: [CommonModule, HtmlVideoPlayerComponent, YoutubePlayerComponent, VimeoPlayerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoTemplateComponent implements CfsTemplateBaseComponent {
  @Input() mediaConfig: CfsTemplateMediaConfig = { ...defaultCfsTemplateMediaConfig, autoplay: true };
  @Input() animationType: TemplateAnimationType = TemplateAnimationType.OnDemandAnimated;
  @Input() displayTime = 0;
  @Input() templateName = "Video Template";

  @Input() set templateProperties(value: TemplateProperties | null) {
    if (value) {
      const { VideoUrl, ImageUrl, VideoContentType, ContentDuration } = value as UrlContentSettings;
      this.url = VideoUrl ?? "";
      this.thumbnailUrl = ImageUrl ?? "";
      this.type = VideoContentType ?? VideoTemplateContentType.Import;
      this.contentDuration = Number(ContentDuration) ?? 0;
    }
  }

  @Output() ready = new EventEmitter();
  @Output() error = new EventEmitter<CfsTemplateError>();

  @ViewChild(YoutubePlayerComponent) youtubePlayer?: YoutubePlayerComponent;
  @ViewChild(VimeoPlayerComponent) vimeoPlayer?: VimeoPlayerComponent;
  @ViewChild(HtmlVideoPlayerComponent) htmlVideoPlayer?: HtmlVideoPlayerComponent;

  readonly VideoTemplateType = VideoTemplateContentType;

  type: VideoTemplateContentType = VideoTemplateContentType.Import;
  url = "";
  thumbnailUrl = "";
  player: VideoPlayerComponent | null = null;
  contentDuration = 0;

  onPlayerReady(): void {
    this.ready.emit();

    this.player = this.getVideoPlayer();
  }

  onPlayerError(error: CfsTemplateError): void {
    this.error.emit(error);
  }

  play(): void {
    if (!this.player) {
      this.player = this.getVideoPlayer();
    }

    this.player?.play();
    console.log(`Playing video: ${this.templateName}`); //todo: remove after testing
  }

  stop(): void {
    if (!this.player) {
      this.player = this.getVideoPlayer();
    }

    this.player?.stop();
    console.log(`Stopping video: ${this.templateName}`);
  }

  pause() {
    if (!this.player) {
      this.player = this.getVideoPlayer();
    }

    this.player?.pause();
    console.log(`Pausing video: ${this.templateName}`);
  }

  restart(): void {
    if (!this.player) {
      this.player = this.getVideoPlayer();
    }

    this.player?.restart();
    console.log(`Restarting video: ${this.templateName}`);
  }

  private getVideoPlayer(): VideoPlayerComponent | null {
    switch (this.type) {
      case VideoTemplateContentType.YouTube:
        return this.youtubePlayer || null;
      case VideoTemplateContentType.Vimeo:
        return this.vimeoPlayer || null;
      case VideoTemplateContentType.Import:
        return this.htmlVideoPlayer || null;
      default:
        return null;
    }
  }
}
